import { i18n } from "../translate/i18n";

/**
 * feat_337 — ¿puede este usuario ABRIR este ticket?
 *
 * ⚠ ES UNA HEURÍSTICA DE UX, NO LA AUTORIDAD. Espeja las vías de
 * `backend/src/services/TicketServices/ShowTicketService.ts`, que sigue siendo
 * el que decide. Su único trabajo es evitar el "flash + 404": decidir ANTES de
 * navegar a /tickets/:id en vez de montar el chat y rebotar con el cartel rojo.
 *
 * 🔴 SI SE TOCAN LAS VÍAS DE ShowTicketService, ACTUALIZAR ESTE ARCHIVO.
 * (Mismo problema que tuvo `buildContactSearchSql` en feat_309.) Si divergen,
 * el error preferible es el conservador: un aviso sobre algo que sí era
 * accesible — molesto, no inseguro. El camino inverso (dejar navegar algo
 * inaccesible) sólo reintroduce el flash que este helper vino a matar.
 *
 * Vías espejadas (numeración de ShowTicketService):
 *   admin  → bypass total (userProfile !== "admin" ni entra al filtro)
 *   vía 1  → el ticket es mío (userId === yo)
 *   vía 2  → huérfano + pending + en una de mis colas
 *   vía 3  → huérfano + canSeeUnassigned + cola compatible o sin cola
 *   vía 4  → yo lo resolví (resolvedByUserId === yo) — archivo personal, feat_185
 *   vía 5  → cerrado de una de mis colas, SÓLO con el pool en modo "queue"
 *   vía 6  → cualquier cerrado/resuelto de la empresa (sin mirar la cola), SÓLO
 *            con el pool en modo "company" — feat_338
 *
 * El modo llega por `opts.pooledMode` ("off" | "queue" | "company"); sin el opt
 * se asume "off". El mapeo del alias legacy del setting ("true"→"queue",
 * "false"→"off") lo hace el call-site, igual que el backend en getPooledMode.
 *
 * Divergencias conocidas y aceptadas:
 *  - vía 0 (sesión de turnos viva, feat_274) NO se espeja: es una excepción por
 *    fila en base que el front no puede ver. Falso negativo para agendador/
 *    profesional dentro de una sesión; ese flujo no pasa por estos call-sites.
 *  - vía 2 en backend exige `queueId IN misColas`; acá `inMyQueue` también da
 *    true con `queueId === null`. Un pending huérfano SIN cola pasa el guard y
 *    el backend lo rechaza salvo que además tenga canSeeUnassigned (vía 3).
 *    Es el único falso positivo posible: reintroduce el flash en ese caso
 *    puntual, no abre nada indebido.
 *
 * @param {object} active  respuesta de GET /contacts/:id/active-ticket
 * @param {object} user    AuthContext.user (id, profile, queues[], canSeeUnassigned)
 * @param {object} opts    { pooledMode: "off"|"queue"|"company" } — setting closedTicketsPooledByQueue
 * @returns {{ ok: boolean, reason?: string, status?: string, queueName?: string, userName?: string }}
 */
/**
 * feat_338 — traduce el valor CRUDO del setting `closedTicketsPooledByQueue` al
 * modo del pool. Espeja EXACTAMENTE `backend/src/helpers/getPooledMode.ts`,
 * alias legacy incluido ("true"→"queue", "false"→"off"), y existe para que ese
 * mapeo esté escrito UNA sola vez en el frontend: lo consumen los 3 call-sites
 * de canOpenTicket + los botones de REABRIR/ATENDER (reasignación de cola).
 *
 * 🔴 Si cambia getPooledMode.ts, cambiar acá también.
 *
 * @param {string|null} value  getSetting("closedTicketsPooledByQueue")
 * @returns {"off"|"queue"|"company"}
 */
export const pooledModeFromSetting = value => {
  if (value === "company") return "company";
  if (value === "queue" || value === "true") return "queue";
  return "off";
};

export const canOpenTicket = (active, user, opts = {}) => {
  if (!active?.exists) return { ok: false, reason: "none" };
  if (user?.profile === "admin") return { ok: true };

  const myQueues = (user?.queues || []).map(q => q.id);
  const uid = Number(user?.id);

  const isMine = active.userId && Number(active.userId) === uid;
  const isResolvedByMe =
    active.resolvedByUserId && Number(active.resolvedByUserId) === uid; // vía 4
  const isOrphan = active.userId == null;
  const inMyQueue = active.queueId == null || myQueues.includes(active.queueId);
  const isClosed = active.status === "closed" || active.status === "resolved";
  const pooledMode = opts.pooledMode || "off";
  // vía 5: el cerrado tiene que ser de una de mis colas.
  const closedPooledQueue =
    pooledMode === "queue" && isClosed && myQueues.includes(active.queueId);
  // vía 6 (feat_338): en modo empresa la cola NO se mira.
  const closedPooledCompany = pooledMode === "company" && isClosed;

  if (isMine || isResolvedByMe) return { ok: true };
  if (isOrphan && active.status === "pending" && inMyQueue) return { ok: true };
  if (isOrphan && user?.canSeeUnassigned && inMyQueue) return { ok: true };
  if (closedPooledQueue || closedPooledCompany) return { ok: true };

  return {
    ok: false,
    reason: "no_access",
    status: active.status,
    queueName: active.queueName,
    userName: active.userName
  };
};

/**
 * feat_337 — texto del aviso cuando `canOpenTicket` dice que no.
 * Compartido por los 3 call-sites para que el mensaje sea el mismo en todos.
 *
 * Dos variantes, según el estado del ticket:
 *  - open/pending CON dueño → alguien lo está atendiendo AHORA: se puede pedir
 *    la transferencia, así que el texto la nombra.
 *  - closed/resolved (o sin dueño) → NO hay a quién pedírselo: el texto sólo
 *    dice de qué cola es. Decir "pedíselo" sobre un cerrado manda al asesor a
 *    molestar a alguien que no tiene nada tomado.
 */
export const noAccessMessage = verdict => {
  const queueName = verdict?.queueName || "—";
  const isActive =
    verdict?.status === "open" || verdict?.status === "pending";

  if (isActive && verdict?.userName) {
    return i18n.t("newTicketModal.inOtherQueueActive", {
      queueName,
      userName: verdict.userName
    });
  }
  return i18n.t("newTicketModal.inOtherQueueClosed", { queueName });
};

export default canOpenTicket;
